export interface ServiceItem {
  id: string;
  name: string;
  category: string;
  subCategory?: string;
  price: number;
  originalPrice: number;
  displayPrice?: string;
  description?: string;
  durationMinutes?: number;
  sortOrder?: number;
}

export function formatDuration(minutes?: number): string {
  if (!minutes || minutes <= 0) return '30 mins';
  if (minutes < 60) return `${minutes} mins`;
  const hrs = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (rem === 0) return `${hrs} hr${hrs > 1 ? 's' : ''}`;
  return `${hrs} hr ${rem} mins`;
}

export function calculateEndTime(startTimeStr: string, durationMinutes: number): string {
  if (!startTimeStr || !durationMinutes) return startTimeStr;
  try {
    const match = startTimeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return startTimeStr;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const meridiem = match[3].toUpperCase();

    if (meridiem === 'PM' && hours < 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;

    const totalMinutes = hours * 60 + minutes + durationMinutes;
    let endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    const endMeridiem = endHours >= 12 ? 'PM' : 'AM';

    if (endHours > 12) endHours -= 12;
    if (endHours === 0) endHours = 12;

    const formattedMinutes = endMinutes < 10 ? `0${endMinutes}` : `${endMinutes}`;
    return `${endHours}:${formattedMinutes} ${endMeridiem}`;
  } catch {
    return startTimeStr;
  }
}

export function getServiceSubCategory(service: { name: string; category: string; subCategory?: string }): string {
  const cat = (service.category || '').toLowerCase();
  // Only Waxing has subcategories per requirement: Hands, Underarms, Legs, Back & Stomach, Combo, and Full Body
  if (cat !== 'waxing') {
    return '';
  }

  if (service.subCategory && service.subCategory.trim()) {
    const s = service.subCategory.trim();
    if (s.toLowerCase() === 'hands' || s.toLowerCase() === 'arms') return 'Hands';
    if (s.toLowerCase() === 'underarms') return 'Underarms';
    if (s.toLowerCase() === 'legs') return 'Legs';
    if (s.toLowerCase().includes('back') || s.toLowerCase().includes('stomach')) return 'Back & Stomach';
    if (s.toLowerCase().includes('full body')) return 'Full Body';
    if (s.toLowerCase().includes('combo')) return 'Combo';
    return s;
  }

  const name = (service.name || '').toLowerCase();

  if (name.includes('full body')) {
    return 'Full Body';
  }
  if (
    (name.includes('arms') && name.includes('legs')) ||
    (name.includes('hands') && name.includes('underarms')) ||
    (name.includes('arms') && name.includes('underarms')) ||
    name.includes('combo')
  ) {
    return 'Combo';
  }
  if (name.includes('underarm')) {
    return 'Underarms';
  }
  if (name.includes('arm') || name.includes('hand')) {
    return 'Hands';
  }
  if (name.includes('leg')) {
    return 'Legs';
  }
  if (name.includes('stomach') || name.includes('back')) {
    return 'Back & Stomach';
  }
  if (name.includes('lip') || name.includes('bean') || name.includes('face')) {
    return 'Combo';
  }

  return 'Hands';
}

export function getServiceWaxType(_service: { name: string }): string {
  return '';
}

export interface BundleItem {
  id: string;
  name: string;
  servicesIncluded: string[];
  price: number;
  originalValue: number;
  saveAmount: number;
  tagColor?: string;
  iconType: 'flower' | 'sparkle' | 'lotus' | 'leaf' | 'heart';
}

export const BLISHKA_BUNDLES: BundleItem[] = [
  {
    id: 'bundle-everyday',
    name: 'Blishka Everyday',
    servicesIncluded: ['Eyebrow', 'Upper Lip', 'Cleanup'],
    price: 499,
    originalValue: 580,
    saveAmount: 81,
    tagColor: '#e07a8b',
    iconType: 'flower'
  },
  {
    id: 'bundle-glow',
    name: 'Blishka Glow',
    servicesIncluded: ['Cleanup', 'Eyebrow', 'Upper Lip', 'Face Massage'],
    price: 599,
    originalValue: 730,
    saveAmount: 131,
    tagColor: '#5c7c64',
    iconType: 'sparkle'
  },
  {
    id: 'bundle-wax-smooth',
    name: 'Blishka Wax & Smooth',
    servicesIncluded: ['Full Arms Wax', 'Full Legs Wax', 'Underarms Wax'],
    price: 699,
    originalValue: 850,
    saveAmount: 151,
    tagColor: '#c47d69',
    iconType: 'lotus'
  },
  {
    id: 'bundle-self-care',
    name: 'Blishka Self-Care',
    servicesIncluded: ['Facial', 'Manicure'],
    price: 899,
    originalValue: 1098,
    saveAmount: 199,
    tagColor: '#5c7c64',
    iconType: 'leaf'
  },
  {
    id: 'bundle-complete',
    name: 'Blishka Complete',
    servicesIncluded: ['Facial', 'Cleanup', 'Full Arms Wax', 'Underarms Wax', 'Eyebrow', 'Upper Lip'],
    price: 1199,
    originalValue: 1450,
    saveAmount: 251,
    tagColor: '#b44e66',
    iconType: 'heart'
  }
];

export interface CategoryItem {
  id: string;
  label: string;
  tagline: string;
  image?: string;
}

export const CATEGORIES: CategoryItem[] = [
  { 
    id: 'all', 
    label: 'All Services',
    tagline: 'Complete doorstep parlour menu'
  },
  { 
    id: 'threading', 
    label: 'Threading',
    image: '/category-threading.jpg',
    tagline: 'Crisp shape, flawless definition'
  },
  { 
    id: 'waxing', 
    label: 'Waxing',
    image: '/category-waxing.jpg',
    tagline: 'Silky smooth, gentle skin wax'
  },
  { 
    id: 'cleanup', 
    label: 'Cleanup & De-Tan',
    image: '/category-cleanup.jpg',
    tagline: 'Deep pore purification & instant radiance'
  },
  { 
    id: 'facial', 
    label: 'Glow Facials',
    image: '/category-facial.jpg',
    tagline: 'Salon-grade luxury brightening & massage'
  },
  { 
    id: 'manicure', 
    label: 'Manicure',
    image: '/category-manicure.jpg',
    tagline: 'Hand scrub, massage & nail care'
  },
  { 
    id: 'pedicure', 
    label: 'Pedicure',
    image: '/category-pedicure.jpg',
    tagline: 'Herbal foot soak & heel renewal'
  },
  { 
    id: 'hairspa', 
    label: 'Hair Spa',
    image: '/category-hairspa.jpg',
    tagline: 'Deep conditioning scalp pampering'
  },
  { 
    id: 'relaxing-massage', 
    label: 'Relaxing Massage',
    image: '/category-relaxing-massage.jpg',
    tagline: 'Deep head, shoulder, back & full body relaxation'
  }
] as const;

export const SERVICES_LIST: ServiceItem[] = [
  {"id":"th-eyebrows","name":"Eyebrows","category":"threading","price":50,"originalPrice":80,"description":"Natural arch & clean shape","durationMinutes":10,"sortOrder":1},
  {"id":"th-upperlip","name":"Upper Lip","category":"threading","price":30,"originalPrice":50,"description":"Quick gentle finish","durationMinutes":10,"sortOrder":2},
  {"id":"th-forehead","name":"Forehead","category":"threading","price":30,"originalPrice":50,"description":"Clean hairline cleanup","durationMinutes":10,"sortOrder":3},
  {"id":"th-chin","name":"Chin","category":"threading","price":30,"originalPrice":50,"description":"Gentle hair removal","durationMinutes":10,"sortOrder":4},
  {"id":"th-fullface","name":"Full Face Threading","category":"threading","price":150,"originalPrice":220,"description":"Brows, upper lip, chin & sides","durationMinutes":25,"sortOrder":5},
  {"id":"wax-underarms","name":"Underarms- Honey Wax","category":"waxing","price":100,"originalPrice":150,"description":"Hygienic & smooth","durationMinutes":15,"sortOrder":100},
  {"id":"srv-1790611155743-2q9y","name":"Underarms - White Chocolate wax","category":"waxing","price":119,"originalPrice":189,"description":"Gentle, low-pain white chocolate wax for sensitive underarm skin.","durationMinutes":15,"sortOrder":102},
  {"id":"srv-1790611198891-6uxw","name":"Underarms - Rica Wax","category":"waxing","price":179,"originalPrice":229,"description":"Premium Italian liposoluble wax for sensitive skin with zero redness.","durationMinutes":15,"sortOrder":104},
  {"id":"wax-halfarms","name":"Half Arms - Honey Wax","category":"waxing","price":150,"originalPrice":220,"description":"Smooth up to elbows","durationMinutes":20,"sortOrder":106},
  {"id":"srv-1790611395738-nqpm","name":"Half Arms - White Chocolate wax","category":"waxing","price":179,"originalPrice":250,"description":"Gentle smooth waxing up to elbows with soothing post-wax finish.","durationMinutes":20,"sortOrder":108},
  {"id":"srv-1790611480335-2fle","name":"Half Hands - Rica wax","category":"waxing","price":399,"originalPrice":500,"description":"Colophony-free Italian wax for sensitive arms with superior grip.","durationMinutes":20,"sortOrder":110},
  {"id":"wax-fullarms","name":"Full Arms - Honey Wax","category":"waxing","price":250,"originalPrice":350,"description":"Full arm silk finish","durationMinutes":30,"sortOrder":112},
  {"id":"wax-arms-underarms","name":"Full Arms + Underarms - Honey wax","category":"waxing","price":300,"originalPrice":450,"description":"Popular grooming combo","durationMinutes":40,"sortOrder":114},
  {"id":"srv-1790611667233-fmd8","name":"Full Hands + Underarms  - White Chocolate wax","category":"waxing","price":399,"originalPrice":549,"description":"Nourishing white chocolate combo for complete arm smooth finish.","durationMinutes":40,"sortOrder":116},
  {"id":"srv-1790611627169-dsc6","name":"Full Hands + Underarms - Rica wax","category":"waxing","price":519,"originalPrice":600,"description":"Complete arm and underarm grooming with premium Italian Rica wax.","durationMinutes":40,"sortOrder":118},
  {"id":"wax-halflegs","name":"Half Legs - Honey Wax","category":"waxing","price":200,"originalPrice":300,"description":"Smooth up to knees","durationMinutes":25,"sortOrder":120},
  {"id":"srv-1790611526584-5i5b","name":"Half Legs  - White Chocolate wax","category":"waxing","price":299,"originalPrice":449,"description":"Painless moisturizing wax up to knees for super soft skin.","durationMinutes":25,"sortOrder":122},
  {"id":"srv-1790611556283-kgr4","name":"Half Legs - Rica wax","category":"waxing","price":399,"originalPrice":600,"description":"Removes even fine, short hair from knees to ankles with minimal pull.","durationMinutes":25,"sortOrder":124},
  {"id":"wax-fulllegs","name":"Full Legs- Honey Wax","category":"waxing","price":350,"originalPrice":499,"description":"Silky smooth legs","durationMinutes":40,"sortOrder":126},
  {"id":"wax-arms-legs","name":"Full Arms + Full Legs - Honey wax","category":"waxing","price":600,"originalPrice":849,"description":"Complete limb smooth combo","durationMinutes":60,"sortOrder":128},
  {"id":"srv-1790611718801-syqs","name":"Upper Lips - Bean wax","category":"waxing","price":59,"originalPrice":120,"description":"Peel-off strip-less bean wax specially formulated for delicate facial skin.","durationMinutes":10,"sortOrder":130},
  {"id":"srv-1790611779566-pzgw","name":"Stomach - Honey wax","category":"waxing","price":199,"originalPrice":249,"description":"Gentle natural honey wax for smooth, hair-free stomach area.","durationMinutes":20,"sortOrder":132},
  {"id":"srv-1790611811681-i1z8","name":"Stomach - Rica wax","category":"waxing","price":399,"originalPrice":599,"description":"Ultra-mild Italian wax for delicate stomach skin with zero irritation.","durationMinutes":20,"sortOrder":134},
  {"id":"srv-1790611742781-udye","name":"Back - Honey wax","category":"waxing","price":199,"originalPrice":299,"description":"Classic warm honey wax for a clean, smooth, hair-free back.","durationMinutes":25,"sortOrder":136},
  {"id":"srv-1790611761332-40hh","name":"Back - Rica wax","category":"waxing","price":399,"originalPrice":549,"description":"Gentle liposoluble wax with vegetable oils for smooth, soft full back.","durationMinutes":25,"sortOrder":138},
  {"id":"wax-fullbody","name":"Full Body Wax","category":"waxing","price":1599,"originalPrice":2199,"description":"Head-to-toe smooth glow","durationMinutes":90,"sortOrder":140},
  {"id":"cf-basic-cleanup","name":"Basic Cleanup","category":"cleanup","price":349,"originalPrice":499,"description":"Pore cleansing & gentle scrub","durationMinutes":30,"sortOrder":14},
  {"id":"cf-glow-cleanup","name":"Glow Cleanup","category":"cleanup","price":449,"originalPrice":649,"description":"Instant brightness & de-tan","durationMinutes":35,"sortOrder":15},
  {"id":"srv-1790614987113-lemg","name":"Face De-tan","category":"cleanup","price":199,"originalPrice":349,"description":"Instant sun tan removal with soothing skin-brightening herbal pack.","durationMinutes":20,"sortOrder":99},
  {"id":"srv-1790615050046-n0tj","name":"Full Hands De-tan","category":"cleanup","price":249,"originalPrice":599,"description":"Deep de-tan treatment for arms, elbows and hands to restore natural glow.","durationMinutes":25,"sortOrder":99},
  {"id":"srv-1790615102209-qiwz","name":"Half Legs De-tan","category":"cleanup","price":299,"originalPrice":599,"description":"Removes stubborn sun tan and pigmentation from knees to feet.","durationMinutes":30,"sortOrder":99},
  {"id":"srv-1790615183592-vras","name":"Back De-tan","category":"cleanup","price":199,"originalPrice":399,"description":"Gentle exfoliating pack to clear back tanning and even out skin tone.","durationMinutes":25,"sortOrder":99},
  {"id":"srv-1790615271456-ym8l","name":"Face Bleach","category":"cleanup","price":199,"originalPrice":399,"description":"Mild golden bleach for instant brightness and radiant facial glow.","durationMinutes":20,"sortOrder":99},
  {"id":"srv-1790615305572-9e1p","name":"Full Hands Bleach","category":"cleanup","price":259,"originalPrice":359,"description":"Even-tone skin lightening and golden sheen for full hands and arms.","durationMinutes":25,"sortOrder":99},
  {"id":"srv-1790615358838-3amo","name":"Half Legs Bleach","category":"cleanup","price":259,"originalPrice":359,"description":"Quick skin brightening and golden glow for lower legs.","durationMinutes":25,"sortOrder":99},
  {"id":"cf-basic-facial","name":"Aroma Fruit Facial","category":"facial","price":599,"originalPrice":849,"description":"Hydrating daily glow massage","durationMinutes":45,"sortOrder":200},
  {"id":"srv-1790612006574-b9ey","name":"Herbal Natural Facial","category":"facial","price":599,"originalPrice":849,"description":"Gentle organic herb extracts for deep hydration and pure, refreshed skin.","durationMinutes":45,"sortOrder":202},
  {"id":"srv-1790612076992-gr0t","name":"Aroma Papaya Facial","category":"facial","price":599,"originalPrice":799,"description":"Rich natural papaya enzymes to clear blemishes and boost radiant skin.","durationMinutes":45,"sortOrder":204},
  {"id":"srv-1790612757077-d8p1","name":"VLCC Glow Facial","category":"facial","price":799,"originalPrice":999,"description":"Salon-trusted deep nourishment and vitamin infusion for luminous glow.","durationMinutes":50,"sortOrder":206},
  {"id":"srv-1790612835326-9sk0","name":"Lotus Bright Facial","category":"facial","price":799,"originalPrice":999,"description":"Herbal white-glow formula to eliminate dullness and revive complexion.","durationMinutes":50,"sortOrder":208},
  {"id":"srv-1790612892538-xypj","name":"Aroma Bright Facial","category":"facial","price":899,"originalPrice":999,"description":"Calming essential oils to hydrate, brighten, and refresh tired skin.","durationMinutes":50,"sortOrder":210},
  {"id":"srv-1790613734487-76ie","name":"Lotus Anti Aging Facial","category":"facial","price":899,"originalPrice":1299,"description":"Firming collagen treatment to smooth fine lines and restore elasticity.","durationMinutes":55,"sortOrder":212},
  {"id":"cf-premium-o3","name":"O+3 Bright Facial","category":"facial","price":999,"originalPrice":1599,"description":"Salon-grade luxury brightening","durationMinutes":60,"sortOrder":214},
  {"id":"srv-1790613011771-z0d1","name":"O+3 Bridal Premium Glow Facial","category":"facial","price":1499,"originalPrice":2199,"description":"Luxury oxygenated bridal treatment for long-lasting HD camera glow.","durationMinutes":75,"sortOrder":216},
  {"id":"mani-spa","name":"VLCC Spa Manicure","category":"manicure","price":699,"originalPrice":899,"description":"Aromatic soak & deep hand mask","durationMinutes":45,"sortOrder":99},
  {"id":"srv-1790614031884-9en2","name":"Aroma Manicure","category":"manicure","price":599,"originalPrice":899,"description":"Aromatic floral soak, gentle cuticle care, and relaxing hand massage.","durationMinutes":40,"sortOrder":99},
  {"id":"pedi-basic","name":"Aroma Pedicure","category":"pedicure","price":599,"originalPrice":899,"description":"Foot soak, heel scrub & massage","durationMinutes":45,"sortOrder":99},
  {"id":"pedi-spa","name":"VLCC Spa Pedicure","category":"pedicure","price":699,"originalPrice":899,"description":"Herbal bath, mask & reflexology","durationMinutes":55,"sortOrder":99},
  {"id":"hair-spa","name":"Loreal Hair Spa","category":"hairspa","price":599,"originalPrice":899,"displayPrice":"₹599 – ₹799","description":"Cream bath, steam & relaxing scalp massage","durationMinutes":45,"sortOrder":99},
  {"id":"srv-1790614193113-htpg","name":"Keratine Hair Spa","category":"hairspa","price":799,"originalPrice":999,"description":"Deep keratin protein infusion for smooth, frizz-free, glossy hair.","durationMinutes":60,"sortOrder":99},
  {"id":"srv-1790614375810-v4vm","name":"Head Massage","category":"relaxing-massage","price":499,"originalPrice":799,"description":"30 mins of relaxing massage of full head.","durationMinutes":30,"sortOrder":99},
  {"id":"srv-1790614431108-0jm8","name":"Shoulder Massage","category":"relaxing-massage","price":499,"originalPrice":899,"description":"30 mins of relaxing shoulder massage","durationMinutes":30,"sortOrder":99},
  {"id":"srv-1790614493556-1agq","name":"Back Massage","category":"relaxing-massage","price":499,"originalPrice":899,"description":"30 mins of relaxing back massage.","durationMinutes":30,"sortOrder":99},
  {"id":"srv-1790614636936-jflu","name":"Full Body Polishing","category":"relaxing-massage","price":2499,"originalPrice":3599,"description":"60 mins of relaxing massage from head to toe.","durationMinutes":60,"sortOrder":99}
];

export const TRUST_POINTS = [
  { title: 'At Your Home Comfort', desc: 'Relax, refresh, and feel pampered in your own private space.' },
  { title: 'Save Time & Travel', desc: 'No parlor rush or traffic. Quick booking & on-time arrival.' },
  { title: 'Professional Expert Service', desc: 'Skilled, verified & courteous female beauty professionals.' },
  { title: 'Best Quality Products', desc: 'Premium, skin-tested branded products with complete transparency.' },
  { title: 'Hygienic & Safe', desc: 'Single-use disposables, sanitization & clean kit for complete peace of mind.' },
  { title: 'At Budget Pricing', desc: 'Transparent salon menu pricing, free doorstep visit (min ₹300), and zero hidden charges.' }
];

export const CONTACT_INFO = {
  phone: import.meta.env.VITE_CONTACT_PHONE || '+918815366617',
  displayPhone: import.meta.env.VITE_CONTACT_DISPLAY_PHONE || '+91 88153 66617',
  whatsapp: import.meta.env.VITE_WHATSAPP_NUMBER || '918815366617',
  serviceAreas: import.meta.env.VITE_SERVICE_AREAS || 'Bhilai and Durg (Chhattisgarh)',
  hours: '9:00 AM – 7:30 PM (All 7 Days)'
};
